import React, { memo, useMemo } from 'react';
import { Checkbox, Flex, Text } from '@mantine/core';
import { SecuritySummary, SecurityType } from '@dealstack/shared/lib/generated';
import { CapChartSecuritiesAccordionContent } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/CapChartSecuritiesDrawer/CapChartSecuritiesAccordionContent.tsx';
import { makeSecurityTypeName } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';

const SecurityCheckboxRow = ({
	label,
	textSize,
	isChecked,
	handleOnChange,
	handleOnClick,
}: {
	label: string;
	textSize?: string;
	isChecked?: boolean;
	handleOnChange: () => void;
	handleOnClick?: (ev: React.MouseEvent) => void;
}) => {
	return (
		<Flex gap="0.5rem" align="center">
			<Checkbox checked={isChecked} onChange={handleOnChange} onClick={handleOnClick} />
			<Text size={textSize} c="dark.4">
				{label}
			</Text>
		</Flex>
	);
};

interface Props {
	type: SecurityType;
	personSecurities: SecuritySummary[];
	selectedSecurities: Map<string, boolean>;
	setSelectSecurities: (securityIds: string[], value: boolean) => void;
}

const SecuritiesByTypeAccordion = ({
	type,
	personSecurities = [],
	selectedSecurities,
	setSelectSecurities,
}: Props) => {
	const isAllSelected = useMemo(
		() => personSecurities.every((s) => selectedSecurities.get(s.id)),
		[selectedSecurities]
	);

	return (
		<CapChartSecuritiesAccordionContent
			value={type}
			noHover
			controlElement={
				<SecurityCheckboxRow
					label={makeSecurityTypeName(type)}
					textSize="B6Bold"
					isChecked={isAllSelected}
					handleOnChange={() => {
						setSelectSecurities(
							personSecurities.map((s) => s.id),
							!isAllSelected
						);
					}}
					handleOnClick={(ev) => {
						ev.stopPropagation();
					}}
				/>
			}
		>
			<Flex direction="column" gap="0.5rem">
				{personSecurities.map((s) => (
					<SecurityCheckboxRow
						key={s.id}
						label={s.name}
						handleOnChange={() =>
							setSelectSecurities([s.id], !selectedSecurities.get(s.id))
						}
						isChecked={selectedSecurities.get(s.id)}
					/>
				))}
			</Flex>
		</CapChartSecuritiesAccordionContent>
	);
};

const Memoized = memo(SecuritiesByTypeAccordion);

export { Memoized as SecuritiesByTypeAccordion };
